import React, { useEffect, useState } from "react";
import useUrlState from "../../hooks/useUrlState";
import useScrollRestoration from "../../hooks/useScrollRestoration";
import { slugifyTab } from "../../utils/tabSlug";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  MenuItem,
  IconButton,
  CircularProgress,
  LinearProgress,
  Chip,
  Divider,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { Add, Close, DirectionsBoat, Delete, Download } from "@mui/icons-material";
import { useSelector } from "react-redux";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";

import api from "../../api/axiosInstance";
import sailingService from "../../services/sailingService";
import StatusChip from "../../components/common/StatusChip";
import PageHeader from "../../components/common/PageHeader";
import EmptyState from "../../components/common/EmptyState";
import SkeletonLoader from "../../components/common/SkeletonLoader";
import AssignShipmentsDialog from "../../components/sailing/AssignShipmentsDialog";
import { selectStatusOptions } from "../../redux/slices/statusDefinitionsSlice";

import { VESSEL_OPTIONS, PORT_OPTIONS } from "../../constants/sailingOptions";

// Short port codes for the sailing list row — the full "PORT EVERGLADES,
// FLA → GEORGE TOWN, GRAND CAYMAN" style route took up a lot of space for
// something that's the same on nearly every sailing. Falls back to the
// first 3 letters of the port name for anything not in this map.
const PORT_CODES = {
  "port everglades, fl": "USPEF",
  "port everglades, fla": "USPEF",
  "george town, cayman islands": "KYGEC",
  "george town, grand cayman": "KYGEC",
};
const portCode = (port) => {
  if (!port) return "—";
  return PORT_CODES[port.trim().toLowerCase()] || port.trim().slice(0, 3).toUpperCase();
};

const SailingFormDialog = ({ open, onClose, onSubmit, initial, loading }) => {
  const isEdit = !!initial;
  const sailingStatusOptions = useSelector(selectStatusOptions("sailing"));
  const blank = {
    vesselName: "",
    voyageNumber: "",
    originPort: "Port Everglades, FL",
    destinationPort: "George Town, Cayman Islands",
    departureDate: "",
    estimatedArrivalDate: "",
    containerNumber: "",
    notes: "",
    status: "scheduled",
    preCleared: false,
    masterBillNumber: "",
    bookingNumber: "",
    freightTerms: "COLLECT",
    totalFreightAndChargesUsd: "",
    totalFreightAndChargesCi: "",
    craneSurchargeAmount: "",
    freightExcludingCraneSurcharge: "",
    freightCharges: [],
  };
  const [form, setForm] = useState(blank);

  useEffect(() => {
    setForm(
      initial
        ? {
          vesselName: initial.vesselName || "",
          voyageNumber: initial.voyageNumber || "",
          originPort: initial.originPort || "Port Everglades, FL",
          destinationPort:
            initial.destinationPort || "George Town, Cayman Islands",
          departureDate: initial.departureDate
            ? dayjs(initial.departureDate).format("YYYY-MM-DD")
            : "",
          estimatedArrivalDate: initial.estimatedArrivalDate
            ? dayjs(initial.estimatedArrivalDate).format("YYYY-MM-DD")
            : "",
          containerNumber: initial.containerNumber || "",
          notes: initial.notes || "",
          status: initial.status || "scheduled",
          preCleared: !!initial.preCleared,
          masterBillNumber: initial.masterBillNumber || "",
          bookingNumber: initial.bookingNumber || "",
          freightTerms: initial.freightTerms || "COLLECT",
          totalFreightAndChargesUsd: initial.totalFreightAndChargesUsd ?? "",
          totalFreightAndChargesCi: initial.totalFreightAndChargesCi ?? "",
          craneSurchargeAmount: initial.craneSurchargeAmount ?? "",
          freightExcludingCraneSurcharge: initial.freightExcludingCraneSurcharge ?? "",
          freightCharges: initial.freightCharges?.length ? initial.freightCharges : [],
        }
        : blank,
    );
  }, [initial, open]);

  const set = (f) => (e) => setForm((p) => ({ ...p, [f]: e.target.value }));

  const setChargeField = (idx, field) => (e) =>
    setForm((p) => ({
      ...p,
      freightCharges: p.freightCharges.map((c, i) => (i === idx ? { ...c, [field]: e.target.value } : c)),
    }));
  const addCharge = () =>
    setForm((p) => ({ ...p, freightCharges: [...p.freightCharges, { label: "", amount: "" }] }));
  const removeCharge = (idx) =>
    setForm((p) => ({ ...p, freightCharges: p.freightCharges.filter((_, i) => i !== idx) }));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography fontWeight={700}>
          {isEdit ? "Edit Sailing" : "New Sailing Schedule"}
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 2.5 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
            <TextField
              select
              label="Vessel *"
              value={form.vesselName}
              onChange={set("vesselName")}
              required
            >
              {VESSEL_OPTIONS.map((v) => (
                <MenuItem key={v} value={v}>
                  {v}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Voyage Number"
              value={form.voyageNumber}
              onChange={set("voyageNumber")}
              placeholder="e.g. 063SB"
            />
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
            <TextField
              label="Container Number"
              value={form.containerNumber}
              onChange={set("containerNumber")}
            />
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
            <TextField
              select
              label="Origin Port"
              value={form.originPort}
              onChange={set("originPort")}
            >
              {PORT_OPTIONS.map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Destination Port"
              value={form.destinationPort}
              onChange={set("destinationPort")}
            >
              {PORT_OPTIONS.map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <TextField
              label="Departure Date *"
              type="date"
              value={form.departureDate}
              onChange={set("departureDate")}
              slotProps={{ inputLabel: { shrink: true } }}
              required
              sx={{
                "& .MuiInputLabel-root": {
                  backgroundColor: "background.paper",
                  px: 0.5,
                  transform: "translate(14px, -9px) scale(0.75)",
                },
              }}
            />
            <TextField
              label="Est. Arrival Date *"
              type="date"
              value={form.estimatedArrivalDate}
              onChange={set("estimatedArrivalDate")}
              slotProps={{ inputLabel: { shrink: true } }}
              required
              sx={{
                "& .MuiInputLabel-root": {
                  backgroundColor: "background.paper",
                  px: 0.5,
                  transform: "translate(14px, -9px) scale(0.75)",
                },
              }}
            />
          </Box>
          {isEdit && (
            <>
              <TextField
                select
                label="Status"
                value={form.status}
                onChange={set("status")}
              >
                {sailingStatusOptions.map((s) => (
                  <MenuItem key={s.value} value={s.value}>
                    {s.label}
                  </MenuItem>
                ))}
              </TextField>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.preCleared}
                    onChange={(e) => setForm((p) => ({ ...p, preCleared: e.target.checked }))}
                  />
                }
                label="Pre-Cleared (already cleared by Waypoint Imports before arrival — skips straight to Customs Cleared on arrival)"
                sx={{ mt: -1 }}
              />
            </>
          )}

          <Divider sx={{ my: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              BOL / Freight Details
            </Typography>
          </Divider>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
            <TextField
              label="Master B/L No."
              value={form.masterBillNumber}
              onChange={set("masterBillNumber")}
            />
            <TextField
              label="Booking No."
              value={form.bookingNumber}
              onChange={set("bookingNumber")}
            />
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
            <TextField
              select
              label="Freight Terms"
              value={form.freightTerms}
              onChange={set("freightTerms")}
            >
              <MenuItem value="COLLECT">COLLECT</MenuItem>
              <MenuItem value="PREPAID">PREPAID</MenuItem>
            </TextField>
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
            <TextField
              label="Total Freight & Charges (US$)"
              type="number"
              value={form.totalFreightAndChargesUsd}
              onChange={set("totalFreightAndChargesUsd")}
            />
            <TextField
              label="Total Freight & Charges (CI$)"
              type="number"
              value={form.totalFreightAndChargesCi}
              onChange={set("totalFreightAndChargesCi")}
            />
            <TextField
              label="Cayman Crane Surcharge"
              type="number"
              value={form.craneSurchargeAmount}
              onChange={set("craneSurchargeAmount")}
            />
            <TextField
              label="Freight Excluding Crane Surcharge"
              type="number"
              value={form.freightExcludingCraneSurcharge}
              onChange={set("freightExcludingCraneSurcharge")}
            />
          </Box>

          <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Itemized Charges
              </Typography>
              <Button size="small" startIcon={<Add />} onClick={addCharge}>
                Add Charge
              </Button>
            </Box>
            {form.freightCharges.length === 0 ? (
              <Typography variant="caption" color="text.disabled">
                No itemized charges — add one, or leave blank and use the totals above.
              </Typography>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {form.freightCharges.map((c, idx) => (
                  <Box key={idx} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <TextField
                      size="small"
                      label="Label"
                      value={c.label}
                      onChange={setChargeField(idx, "label")}
                      sx={{ flex: 2 }}
                    />
                    <TextField
                      size="small"
                      label="Amount"
                      type="number"
                      value={c.amount}
                      onChange={setChargeField(idx, "amount")}
                      sx={{ flex: 1 }}
                    />
                    <IconButton size="small" color="error" onClick={() => removeCharge(idx)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          <TextField
            label="Notes (optional)"
            multiline
            rows={2}
            value={form.notes}
            onChange={set("notes")}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={() => onSubmit(form)}
          variant="contained"
          disabled={
            !form.vesselName ||
            !form.departureDate ||
            !form.estimatedArrivalDate ||
            loading
          }
        >
          {loading ? (
            <CircularProgress size={18} color="inherit" />
          ) : isEdit ? (
            "Save Changes"
          ) : (
            "Create Sailing"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const SailingRow = ({ s, onEdit, onAssign, onDelete }) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const canAssign = !["arrived", "completed"].includes(s.status);
  const canDelete = (s.items?.length || 0) === 0;
  const [downloadingDocuments, setDownloadingDocuments] = useState(false);

  // Manifest + MBL (if uploaded) + every customer's HBOL + every customer's
  // invoice, zipped together — see sailingController.getSailingDocumentsZip.
  const handleDownloadDocuments = async (e) => {
    e.stopPropagation();
    setDownloadingDocuments(true);
    try {
      const response = await sailingService.getDocumentsZip(s._id);
      const disposition = response.headers?.["content-disposition"] || "";
      const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
      const filename = filenameMatch ? filenameMatch[1] : `${s.sailingNumber}-documents.zip`;

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      enqueueSnackbar("Failed to download documents", { variant: "error" });
    } finally {
      setDownloadingDocuments(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        px: 2.5,
        py: 2,
        borderBottom: "1px solid",
        borderColor: "divider",
        "&:last-child": { borderBottom: "none" },
      }}
    >
      {/* Icon */}
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 2,
          bgcolor: ["completed", "arrived"].includes(s.status)
            ? "action.selected"
            : "#001A3C",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <DirectionsBoat
          sx={{
            color: ["completed", "arrived"].includes(s.status)
              ? "text.disabled"
              : "white",
            fontSize: 22,
          }}
        />
      </Box>

      {/* Info */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            mb: 0.25,
          }}
        >
          <Typography
            fontWeight={700}
            fontSize={13}
            onClick={() => navigate(`/admin/sailing/${s.sailingNumber}`)}
            sx={{
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {s.vesselName}
            {s.voyageNumber ? ` / ${s.voyageNumber}` : ""}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontFamily: "monospace" }}
          >
            #{s.sailingNumber}
          </Typography>
          <StatusChip status={s.status} domain="sailing" />
        </Box>
        <Typography variant="caption" color="text.secondary" component="div">
          {portCode(s.originPort)} → {portCode(s.destinationPort)}
          {s.containerNumber ? ` · Container: ${s.containerNumber}` : ""}
          {s.masterBillNumber ? (
            <>
              {" · "}
              <Typography component="span" variant="caption" fontWeight={700}>
                B/L:
              </Typography>{" "}
              {s.masterBillNumber}
            </>
          ) : null}
        </Typography>
      </Box>

      {/* Package count */}
      <Box sx={{ textAlign: "center", flexShrink: 0, minWidth: 70 }}>
        <Typography fontWeight={700} fontSize={18}>
          {s.items?.length || 0}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          items
        </Typography>
      </Box>

      {/* Dates */}
      <Box sx={{ textAlign: "right", flexShrink: 0, minWidth: 90 }}>
        <Typography fontWeight={700} fontSize={13} color="primary.main">
          {dayjs(s.departureDate).format("MMM D")}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          ETA {dayjs(s.estimatedArrivalDate).format("MMM D")}
        </Typography>
      </Box>

      {/* Actions */}
      <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
        {canAssign && (
          <Button
            size="small"
            variant="contained"
            sx={{ fontSize: 11 }}
            startIcon={<Add sx={{ fontSize: 14 }} />}
            onClick={() => onAssign(s)}
          >
            Assign
          </Button>
        )}
        <Button
          size="small"
          variant="outlined"
          sx={{ fontSize: 11 }}
          onClick={() => onEdit(s)}
        >
          Edit
        </Button>
        <Tooltip title="Download Manifest, MBL, HBOLs & Invoices">
          <span>
            <IconButton
              size="small"
              disabled={downloadingDocuments || (s.items?.length || 0) === 0}
              onClick={handleDownloadDocuments}
            >
              {downloadingDocuments ? <CircularProgress size={16} /> : <Download fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={canDelete ? "Delete sailing" : "Unassign all shipments before deleting"}>
          <span>
            <IconButton
              size="small"
              color="error"
              disabled={!canDelete}
              onClick={() => onDelete(s)}
            >
              <Delete fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
};

const SAILING_URL_SCHEMA = {
  // The tab's slugified label ("all", "in_transit", …) rather than its
  // array index — stable if tabs are ever reordered, and readable in the
  // address bar/shared links instead of a meaningless number.
  tab: { default: "" },
};

const AdminSailing = () => {
  const { enqueueSnackbar } = useSnackbar();
  const sailingStatusOptions = useSelector(selectStatusOptions("sailing"));
  const [sailings, setSailings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [assignTarget, setAssignTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);
  useScrollRestoration();
  const [{ tab }, setUrlState] = useUrlState(SAILING_URL_SCHEMA);

  // "All" excludes completed sailings — those clutter the default view
  // once there are more than a handful; they still have their own tab.
  const TABS = [
    { label: "All", value: "" },
    ...sailingStatusOptions.map((s) => ({ label: s.label, value: s.value })),
  ];
  const activeTab = Math.max(0, TABS.findIndex((t) => slugifyTab(t.label) === tab));
  const activeTabValue = TABS[activeTab]?.value || "";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/sailing");
      setSailings(data.data?.schedules || []);
    } catch {
      enqueueSnackbar("Failed to load sailing schedules", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSubmit = async (form) => {
    setSaving(true);
    try {
      const numOrUndefined = (v) => (v === "" || v == null ? undefined : Number(v));
      const payload = {
        ...form,
        totalFreightAndChargesUsd: numOrUndefined(form.totalFreightAndChargesUsd),
        totalFreightAndChargesCi: numOrUndefined(form.totalFreightAndChargesCi),
        craneSurchargeAmount: numOrUndefined(form.craneSurchargeAmount),
        freightExcludingCraneSurcharge: numOrUndefined(form.freightExcludingCraneSurcharge),
        freightCharges: (form.freightCharges || [])
          .filter((c) => c.label?.trim())
          .map((c) => ({ label: c.label.trim(), amount: Number(c.amount) || 0 })),
      };

      if (editTarget) {
        await api.patch(`/sailing/${editTarget._id}`, payload);
        enqueueSnackbar("Sailing updated", { variant: "success" });
      } else {
        await api.post("/sailing", payload);
        enqueueSnackbar("Sailing schedule created", { variant: "success" });
      }
      setFormOpen(false);
      setEditTarget(null);
      load();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to save", {
        variant: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/sailing/${deleteTarget._id}`);
      enqueueSnackbar(`${deleteTarget.sailingNumber} deleted`, { variant: "success" });
      setDeleteTarget(null);
      load();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to delete sailing", {
        variant: "error",
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleAssigned = () => load();

  const openEdit = (s) => {
    setEditTarget(s);
    setFormOpen(true);
  };
  const openAssign = (s) => setAssignTarget(s);

  const active = sailings.filter((s) =>
    ["scheduled", "loading", "sailing"].includes(s.status),
  );

  const statCount = (status) =>
    sailings.filter((s) => s.status === status).length;

  const filteredSailings = sailings
    .filter((s) => (activeTabValue ? s.status === activeTabValue : s.status !== "completed"))
    // Descending — furthest-out/soonest-upcoming departures sit above ones
    // that have already departed, rather than the oldest date floating to
    // the top.
    .sort((a, b) => new Date(b.departureDate) - new Date(a.departureDate));

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title="Sailing Schedules"
        subtitle={`${active.length} active · ${sailings.length} total`}
        action={
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              setEditTarget(null);
              setFormOpen(true);
            }}
          >
            New Sailing
          </Button>
        }
      />

      {/* Summary */}
      <Box
        sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2 }}
      >
        {[
          {
            label: "Scheduled",
            status: "scheduled",
            color: "#2B6CB0",
            bg: "#EBF4FF",
          },
          {
            label: "Loading",
            status: "loading",
            color: "#C05621",
            bg: "#FFFBEB",
          },
          {
            label: "At Sea",
            status: "sailing",
            color: "#553C9A",
            bg: "#E9D8FD",
          },
          {
            label: "Completed",
            status: "completed",
            color: "#0E9F6E",
            bg: "#ECFDF5",
          },
        ].map((s) => (
          <Card
            key={s.label}
            sx={{ p: 2, display: "flex", alignItems: "center", gap: 1.5 }}
          >
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: 1.5,
                bgcolor: s.bg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <DirectionsBoat sx={{ fontSize: 20, color: s.color }} />
            </Box>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                {s.label}
              </Typography>
              <Typography fontWeight={700} fontSize={20}>
                {loading ? "—" : statCount(s.status)}
              </Typography>
            </Box>
          </Card>
        ))}
      </Box>

      {/* Sailings, filterable by status */}
      <Card>
        <Box sx={{ px: 1, borderBottom: "1px solid", borderColor: "divider" }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setUrlState({ tab: slugifyTab(TABS[v].label) })}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              "& .MuiTab-root": {
                fontSize: 13,
                textTransform: "none",
                fontWeight: 600,
                minHeight: 44,
              },
            }}
          >
            {TABS.map((t, i) => (
              <Tab key={i} label={t.label} />
            ))}
          </Tabs>
        </Box>
        {loading ? (
          <SkeletonLoader rows={4} />
        ) : filteredSailings.length === 0 ? (
          <EmptyState
            icon="⛵"
            title="No sailings"
            description="Create a new sailing schedule to assign packages."
            actionLabel="New Sailing"
            onAction={() => {
              setEditTarget(null);
              setFormOpen(true);
            }}
          />
        ) : (
          filteredSailings.map((s) => (
            <SailingRow
              key={s._id}
              s={s}
              onEdit={openEdit}
              onAssign={openAssign}
              onDelete={setDeleteTarget}
            />
          ))
        )}
      </Card>

      {/* Create/Edit Dialog */}
      <SailingFormDialog
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditTarget(null);
        }}
        onSubmit={handleSubmit}
        initial={editTarget}
        loading={saving}
      />

      {/* Assign Dialog */}
      <AssignShipmentsDialog
        open={!!assignTarget}
        sailing={assignTarget}
        onClose={() => setAssignTarget(null)}
        onAssigned={handleAssigned}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Sailing?"
        message={`Delete sailing ${deleteTarget?.sailingNumber} (${deleteTarget?.vesselName})? This cannot be undone.`}
        confirmLabel="Delete"
        confirmColor="error"
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </Box>
  );
};

export default AdminSailing;
